import { Model } from 'app/model/backendmodel';
import { Inject, Injectable } from '@angular/core';
import { Http } from 'app/services/http';
import { LoggerService } from 'app/logger/logger.service';
import { MetadataClientFactoryService } from 'app/services/metadata-client/metadata-client-factory.service';
import { DataPlaneAuthService } from 'app/services/data-plane-auth.service';
import { DataPlaneApiBaseService } from './data-plane-api.service';
import { FactoryUrlInformationService } from 'app/services/factory-url-information.service';
import { WorkspaceUrlInformationService } from 'app/services/workspace-url-information.service';
import {
    IValidateDataFlowRequest,
    IValidateDataFlowPayload,
    IValidateDataFlowResponse,
    IValidateDataFlowExpressionRequest,
    IValidateDataFlowExpressionResponse
} from 'app/interfaces/IDataFlowDebug';
import { DataFlow } from 'app/model/dataflow/dataflow';
import { Named } from 'app/model/base/named';
import { IDataFlow } from 'app/model/adapters/dataflow/IDataflow';
import * as Constants from 'app/services/globals';
import { Walker, DefaultPropertyInspector } from 'app/model/base/walker';
import { Metamodel } from 'app/model/base/model';
import { ModelUtilties } from 'app/model/services/model-utilities';
import { DataFlowExpressionUtils } from 'app/model/adapters/dataflow/expression.util';
import { AdapterContext, IResourceContext } from 'shell-models-lib';
import { RESOURCE_CONTEXT_DATA } from 'app/multi-contexts/resource-context.typings';

@Injectable({
    providedIn: 'root'
})
export class DataFlowDataPlaneService extends DataPlaneApiBaseService {
    public static className: string = 'DataFlowDataPlaneService';
    protected readonly _apiVersion = Constants.GlobalVariables.dataFactoryVersion;

    public constructor(
        @Inject(RESOURCE_CONTEXT_DATA) private readonly _contextData: IResourceContext,
        _httpClient: Http,
        _dataPlaneAuthService: DataPlaneAuthService,
        _metadataClientService: MetadataClientFactoryService,
        adfUrlService: FactoryUrlInformationService,
        a365UrlService: WorkspaceUrlInformationService,
        _logger: LoggerService
    ) {
        super(
            _httpClient,
            _dataPlaneAuthService,
            _metadataClientService,
            __IS_A365__ ? a365UrlService : adfUrlService,
            _logger
        );
    }

    public async validateDataFlow(
        dataFlow: DataFlow,
        getAllSchema: boolean = false
    ): Promise<IValidateDataFlowResponse> {
        const uri = `${await this._getDataPlaneBaseUrl()}/validateDataFlow`;
        const dataFlowPayload = await this._constructDataFlowPayload(dataFlow);
        const requestBody: IValidateDataFlowRequest = {
            command: 'validatepayload',
            payload: {
                payload: `${JSON.stringify(dataFlowPayload)}`,
                payloadName: dataFlow.name,
                getAllSchema
            }
        };
        return await this.sendMessageAsync(uri, 'POST', requestBody);
    }

    public async validateDataFlowExpression(
        dataFlow: DataFlow,
        streamName: string,
        expr: string
    ): Promise<IValidateDataFlowExpressionResponse> {
        const uri = `${await this._getDataPlaneBaseUrl()}/validateDataFlow`;
        const dataFlowPayload = await this._constructDataFlowPayload(dataFlow);
        const unwrappedExpression = DataFlowExpressionUtils.unwrapExpressionWithErrors(expr);
        const requestBody: IValidateDataFlowExpressionRequest = {
            command: 'validateexpression',
            payload: {
                // TODO: remove this when the LS cred issue is fixed
                payload: `${JSON.stringify(dataFlowPayload).replace(
                    /AccountName=(.*);/g,
                    'AccountName=$1;AccountKey=fake;'
                )}`,
                payloadName: dataFlow.name,
                streamName,
                expr: unwrappedExpression
            }
        };
        return await this.sendMessageAsync(uri, 'POST', requestBody);
    }

    private async _constructDataFlowPayload(entity: DataFlow): Promise<IValidateDataFlowPayload> {
        const expressions = entity.getAllExpressions();
        expressions.forEach((dataFlowExpression) => {
            dataFlowExpression.expression = DataFlowExpressionUtils.unwrapExpressionWithErrors(
                dataFlowExpression.expression
            );
        });

        const namedSet: Named[] = [];
        const entities: { [key: string]: Array<{}> } = {};
        const context = AdapterContext.newAdapterContext({
            contextData: this._contextData,
            version: Constants.ADAPTER_VERSION20
        });
        namedSet.push(entity);

        Walker.conceptDependencies({ inlineDataset: false }, DefaultPropertyInspector.instance, entity).forEach(
            (conceptNamed) => {
                const concept = Metamodel.modelOf(conceptNamed).concept;
                if (concept == null) {
                    return;
                }
                namedSet.push(conceptNamed);
            }
        );

        const promises = [];
        for (const fetchedNamed of namedSet) {
            promises.push(this._addNamedToEntity(fetchedNamed, context, entities));
        }

        await Promise.all(promises);

        const dataFlowPayload = {
            name: entity.name,
            properties: {
                dataFlows: entities['dataflows'] as IDataFlow[],
                linkedServices: (entities['linkedServices'] || []) as Model.LinkedServiceResource[],
                datasets: (entities['datasets'] || []) as Model.DatasetResource[]
            }
        };
        expressions.forEach((dataFlowExpression) => {
            dataFlowExpression.expression = DataFlowExpressionUtils.wrapExpressionWithErrors(
                dataFlowExpression.expression
            );
        });
        return dataFlowPayload;
    }

    private async _addNamedToEntity(
        fetchedNamed: Named,
        context: AdapterContext,
        entities: {
            [key: string]: Array<{}>;
        }
    ): Promise<void> {
        const concept = Metamodel.modelOf(fetchedNamed).concept;

        if (concept) {
            const resource = await ModelUtilties.toResources(context, fetchedNamed);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (resource[concept.name] as any).name = context.identityProvider.identity(fetchedNamed);
            entities[concept.name + 's'] = entities[concept.name + 's'] || [];
            entities[concept.name + 's'].push(resource[concept.name]);
        }
    }
}
